import './ProfileItem.scss';

import React, {
  memo,
  useMemo,
} from 'react';

import { Typography } from 'antd';
import { ParagraphProps } from 'antd/lib/typography/Paragraph';
import { TitleProps } from 'antd/lib/typography/Title';

import CustomAvatar from '../CustomAvatar/CustomAvatar';
import ProfileTitle from './ProfileTitle';

const MIN_INDICATOR_SIZE = 8;

// interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
interface Props {
  avatar?: React.ReactNode;
  avatarSize?: number;
  avatarTitle?: string;
  title: React.ReactNode;
  tooltip?: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  showOnline?: boolean;
  isCreator?: boolean;
  titleStyle?: React.CSSProperties;
  subtitleStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  actionStyle?: React.CSSProperties;
  indicatorStyle?: React.CSSProperties;
  className?: string;
  id?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  titleProps?: TitleProps;
  subtitleProps?: ParagraphProps;
}

const ProfileItem: React.FC<Props> = ({
  avatar,
  avatarSize = 40,
  avatarTitle,
  title,
  tooltip = '',
  subtitle,
  action,
  showOnline = false,
  isCreator = false,
  titleStyle,
  subtitleStyle,
  contentStyle,
  actionStyle,
  indicatorStyle,
  className = '',
  id = '',
  titleProps,
  subtitleProps,
  onClick,
  style,
  ...props
}) => {
  const renderedAvatar = useMemo(() => {
    if (!avatar && !avatarTitle) return null;

    if (avatar) {
      return typeof avatar === 'string' ? (
        <CustomAvatar
          size={avatarSize}
          src={avatar}
          name={avatarTitle}
          border={0}
        />
      ) : (
        avatar
      );
    }

    if (avatarTitle) {
      return <CustomAvatar size={avatarSize} name={avatarTitle} border={0} />;
    }

    return null;
  }, [avatar, avatarSize, avatarTitle]);

  return (
    <div
      className={`item__wrapper ${showOnline ? 'item__wrapper--active' : ''} ${className}`}
      id={id}
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
      {...props}>
      {renderedAvatar ? (
        <div className="item__avatar__wrapper">
          {renderedAvatar}

          {showOnline && avatarSize ? (
            <span
              className="item__avatar__active-indicatior"
              style={{
                width:
                  avatarSize / 10 < MIN_INDICATOR_SIZE
                    ? MIN_INDICATOR_SIZE
                    : avatarSize / 10,
                height:
                  avatarSize / 10 < MIN_INDICATOR_SIZE
                    ? MIN_INDICATOR_SIZE
                    : avatarSize / 10,
                right: '5%',
                bottom: '5%',
                ...indicatorStyle,
              }}
            />
          ) : null}
        </div>
      ) : null}

      <div className="item__content" style={contentStyle}>
        <Typography.Title
          level={5}
          className="item__content__title"
          ellipsis={{
            rows: 1,
            tooltip: typeof title === 'string' ? title : tooltip,
          }}
          style={titleStyle}
          {...titleProps}>
          {isCreator ? <ProfileTitle title={title} isCreator /> : title}
        </Typography.Title>

        <div className="item__content__actions">
          {action ? (
            <div
              className="item__action"
              style={actionStyle}
              onClick={(e) => e.stopPropagation()}>
              {action}
            </div>
          ) : null}
          {subtitle ? (
            <Typography.Paragraph
              className="item__content__subtitle"
              ellipsis={{
                rows: 1,
                tooltip: typeof subtitle === 'string' ? subtitle : '',
              }}
              style={subtitleStyle}
              {...subtitleProps}>
              {subtitle}
            </Typography.Paragraph>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default memo(ProfileItem);
